export type ViewLevel = 'ideas' | 'themes';

/**
 * Strict check for the special auto-managed Bagel AI tab.
 * Falls back to name-based detection for backward compatibility
 * with views that haven't been migrated yet.
 */
export const isBagelAiTab = (view: any): boolean =>
  view?.isAiTab ?? (view?.name?.toLowerCase() === 'bagel ai');

/**
 * Broad check for whether a view should render themes content
 * (the auto Bagel AI tab, or any user-saved view with level='themes').
 */
export const isAiView = (view: any): boolean =>
  isBagelAiTab(view) || view?.level === 'themes';

export const hexToRgba = (hex: string, alpha: number): string => {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

export const AI_VIEW_STORAGE_KEY = 'ideas-columns-bagel-ai';

type AiViewSpecEntry =
  | { kind: 'title'; title: string }
  | { kind: 'parentIdeaTitle'; title: string }
  | { kind: 'pickByAccessors'; accessors: string[]; title?: string };

export const AI_VIEW_COLUMN_SPEC: AiViewSpecEntry[] = [
  { kind: 'title', title: 'Capability/Need' },
  { kind: 'parentIdeaTitle', title: 'Parent Idea' },
  {
    kind: 'pickByAccessors',
    accessors: ['productItem.name', 'componentObj.name'],
    title: 'Product Area',
  },
  {
    kind: 'pickByAccessors',
    accessors: [
      'aggregations.accountsCount.total',
      'aggregations.accountsCount.assigned',
      'aggregations.accountsCount.suggested',
    ],
    title: '# Customers',
  },
  {
    kind: 'pickByAccessors',
    accessors: ['aggregations.accountsTotalArr.total'],
  },
];

const PARENT_IDEA_COLUMN_TEMPLATE: Record<string, any> = {
  accessor: 'parentIdeaTitle',
  title: 'Parent Idea',
  label: 'Parent Idea',
  renderComponent: 'TextCell',
  enabled: true,
  hidden: false,
  sortable: false,
  resizable: true,
  draggable: true,
  generatedEnabled: true,
  openIdeaOnCLick: true,
  width: 240,
};

export const buildAiViewColumns = (pageColumns: any[], overrides: any[] = []): any[] => {
  let seq = 1;
  const forceVisible = (col: any) => ({ ...col, hidden: false, enabled: true });
  const defaults = AI_VIEW_COLUMN_SPEC
    .map((spec) => {
      switch (spec.kind) {
      case 'title': {
        const base = pageColumns.find((c: any) => c.accessor === 'title');
        return base
          ? forceVisible({ ...base, title: spec.title, label: spec.title, index: seq++ })
          : null;
      }
      case 'parentIdeaTitle':
        return { ...PARENT_IDEA_COLUMN_TEMPLATE, index: seq++ };
      case 'pickByAccessors': {
        for (const accessor of spec.accessors) {
          const base = pageColumns.find((c: any) => c.accessor === accessor);
          if (base) {
            const picked: any = { ...base, index: seq++ };
            if (spec.title) {
              picked.title = spec.title;
              picked.label = spec.title;
            }
            return forceVisible(picked);
          }
        }
        return null;
      }
      default:
        return null;
      }
    })
    .filter(Boolean) as any[];

  const applied = defaults.map((col: any) => {
    const ov = overrides.find((o: any) => o.accessor === col.accessor);
    if (!ov) return col;
    return {
      ...col,
      hidden: ov.hidden ?? col.hidden,
      enabled: ov.enabled ?? col.enabled,
      index: ov.index ?? col.index,
      width: ov.width ?? col.width,
    };
  });

  // Re-key indices to a unique sequential range so equal-index ties (which can
  // arise from cached overrides written during a previous flicker cycle) don't
  // cause non-deterministic column order across renders.
  const sorted = [...applied].sort((a: any, b: any) => (a.index ?? 0) - (b.index ?? 0));
  return sorted.map((col: any, i: number) => ({ ...col, index: i }));
};

export const toPersistShape = (cols: any[]): any[] =>
  cols.map((c: any) => ({
    accessor: c.accessor,
    hidden: c.hidden ?? false,
    enabled: c.enabled !== false,
    index: c.index,
    width: c.width,
  }));

export const readAiOverrides = (): any[] => {
  try {
    return JSON.parse(localStorage.getItem(AI_VIEW_STORAGE_KEY) || '[]');
  } catch {
    return [];
  }
};

/**
 * Default sort applied to the Bagel AI tab when the URL `sortStatus`
 * references a column that doesn't exist on the AI tab. Single source of
 * truth so FE/BE/tests can reference the same default.
 */
export const AI_VIEW_DEFAULT_SORT = {
  columnAccessor: 'aggregations.accountsCount.total',
  direction: 'desc' as const,
};

/**
 * Returns the list of column accessors that the Bagel AI tab will actually
 * render for the given tenant `pageColumns`. Mirrors the resolution logic in
 * `buildAiViewColumns` so callers can validate whether a `sortStatus`
 * accessor is supported on the AI tab.
 */
export const getAiViewAccessors = (pageColumns: any[]): string[] => {
  const accessors: string[] = [];
  AI_VIEW_COLUMN_SPEC.forEach((spec) => {
    switch (spec.kind) {
    case 'title': {
      const base = pageColumns.find((c: any) => c.accessor === 'title');
      if (base?.accessor) accessors.push(base.accessor);
      break;
    }
    case 'parentIdeaTitle':
      accessors.push(PARENT_IDEA_COLUMN_TEMPLATE.accessor);
      break;
    case 'pickByAccessors': {
      for (const accessor of spec.accessors) {
        const base = pageColumns.find((c: any) => c.accessor === accessor);
        if (base) {
          accessors.push(accessor);
          break;
        }
      }
      break;
    }
    default:
      break;
    }
  });
  return accessors;
};

/**
 * Returns true if two filter arrays represent the same selection.
 * Normalizes keys to lowercase and sorts selected values before comparing.
 */
export function filtersAreSame(a: any[], b: any[]): boolean {
  const norm = (filters: any[]) => {
    const map = new Map<string, string[]>();
    (filters || []).forEach((f: any) => {
      const key = (f?.name || f?.parent || "").toLowerCase();
      const vals = (Array.isArray(f.selected) ? f.selected : [])
        .map((v: any) => (typeof v === "string" ? v.toLowerCase() : String(v)))
        .sort();
      if (vals.length > 0) map.set(key, vals);
    });
    return map;
  };
  const ma = norm(a);
  const mb = norm(b);
  if (ma.size !== mb.size) return false;
  for (const [key, val] of ma) {
    const bVal = mb.get(key);
    if (!bVal || JSON.stringify(val) !== JSON.stringify(bVal)) return false;
  }
  return true;
}

/**
 * Strips pure default filters from activeFilters before saving a view.
 */
export function getFiltersToSave(activeFilters: any[], viewFilters: any[]): any[] {
  const normalizeKey = (f: any) => (f?.name || f?.parent || '').toLowerCase();
  const toLowerArr = (arr: any[]) =>
    (Array.isArray(arr) ? arr : [])
      .map((v: any) => (typeof v === 'string' ? v.toLowerCase() : String(v)))
      .sort();

  const pureDefaults = (viewFilters || []).filter((f: any) => f.default === true);
  if (pureDefaults.length === 0) return activeFilters || [];

  const defaultMap = new Map<string, string[]>();
  pureDefaults.forEach((f: any) => {
    defaultMap.set(normalizeKey(f), toLowerArr(f.selected || []));
  });

  return (activeFilters || []).filter((f: any) => {
    const key = normalizeKey(f);
    if (!defaultMap.has(key)) return true; // not related to any default → keep
    // Same name as a pure default: keep only if user changed the selected values
    const defaultSelected = defaultMap.get(key)!;
    const currentSelected = toLowerArr(f.selected || []);
    return JSON.stringify(currentSelected) !== JSON.stringify(defaultSelected);
  });
}
